/* eslint-disable react-hooks/rules-of-hooks */
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { CardListRow } from '@starjack/shared-components';
import {
    ChangeEvent,
    Fragment,
    FunctionComponent,
    useCallback,
    useEffect,
    useState,
} from 'react';
import {
    getMailTextElementByLanguage,
    MailTextElement,
    MailTextGroup,
    MailTextVariable,
} from '../model/mailConfiguration.types';
import { MailTextConfigMap } from '../model/mailTextConfig';

type MailTextBlockProps = {
    group: MailTextGroup;
    mailTextConfig: MailTextConfigMap;
    onGroupUpdate: (newGroup: MailTextGroup) => void;
    onShowImage: (image: string) => void;
};

export const MailTextBlock: FunctionComponent<MailTextBlockProps> = ({
    group,
    mailTextConfig,
    onGroupUpdate,
    onShowImage,
}) => {
    const [open, setOpen] = useState(false);

    // to prevent performance issues render accordion content only when opened
    const [showContent, setShowContent] = useState(false);
    const onVariableUpdate = (newVariable: MailTextVariable) => {
        const newVariables = [...group.variables];

        const variablesIndex = newVariables.findIndex(
            (variable) => variable.name === newVariable.name
        );

        if (variablesIndex === -1) return;

        newVariables[variablesIndex] = newVariable;

        onGroupUpdate({
            ...group,
            variables: newVariables,
        });
    };

    const configEntry = mailTextConfig[group.name];
    if (!configEntry) return null;

    const sortedVariables = [...group.variables].sort(
        (vA, vB) =>
            configEntry.variables[vA.name]?.sort -
            configEntry.variables[vB.name]?.sort
    );

    return (
        <Accordion
            elevation={2}
            sx={{
                borderRadius: '8px',

                '&:before': {
                    display: 'none',
                },
            }}
            TransitionProps={{
                onExited: () => {
                    // set this to false after closing animation to prevent jumping
                    setShowContent(false);
                },
            }}
            expanded={open}
            onChange={() => {
                setOpen((currentlyOpen) => {
                    if (!currentlyOpen) {
                        setShowContent(true);
                        return true;
                    } else {
                        return false;
                    }
                });
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={(theme) => ({
                    p: 4,

                    borderBottom: '1px solid',
                    borderColor: '#fff',

                    transition: theme.transitions.create(['border-color']),

                    ...(open && {
                        borderColor: 'divider',
                    }),
                })}
            >
                <Typography variant='h6'>{configEntry.name}</Typography>
            </AccordionSummary>

            <CardListRow
                sx={{
                    my: 3,
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 500,
                    }}
                >
                    {configEntry.description}
                </Typography>
            </CardListRow>

            {showContent &&
                sortedVariables.map((variable, index) => {
                    const configEntryVariable =
                        configEntry.variables[variable.name];

                    if (!configEntryVariable) return null;

                    return (
                        <Fragment key={index}>
                            <CardListRow
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: 'divider',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: 500,
                                    }}
                                >
                                    {configEntryVariable.name}
                                </Typography>

                                <Button
                                    sx={{
                                        ml: 3,
                                    }}
                                    onClick={() => {
                                        onShowImage(group.name);
                                    }}
                                >
                                    Musteransicht öffnen
                                </Button>
                            </CardListRow>
                            <CardListRow
                                sx={{
                                    display: 'flex',
                                }}
                            >
                                <MailSnippetDeEn
                                    variable={variable}
                                    onVariableUpdate={onVariableUpdate}
                                />
                            </CardListRow>
                        </Fragment>
                    );
                })}
        </Accordion>
    );
};

type MailSnippetDeEn = {
    variable: MailTextVariable;
    onVariableUpdate: (newVariable: MailTextVariable) => void;
};

const MailSnippetDeEn: FunctionComponent<MailSnippetDeEn> = ({
    variable,
    onVariableUpdate,
}) => {
    const onMailTextElementUpdate =
        (language: 'DE' | 'EN') => (mailTextElement: MailTextElement) => {
            const index = variable.texts.findIndex(
                (e) => e.language === language
            );

            if (index > -1) {
                const textsToUpdate = [...variable.texts];
                textsToUpdate[index] = mailTextElement;
                onVariableUpdate({
                    ...variable,
                    texts: textsToUpdate,
                });
            }
        };

    return (
        <Fragment>
            <Box
                sx={{
                    width: '50%',
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 500,
                    }}
                >
                    Deutsch:
                </Typography>

                <MailTextInput
                    onMailTextElementChange={onMailTextElementUpdate('DE')}
                    mailTextElement={getMailTextElementByLanguage(
                        variable.texts,
                        'DE'
                    )}
                />
            </Box>
            <Box
                sx={{
                    width: '50%',
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 500,
                    }}
                >
                    Englisch:
                </Typography>

                <MailTextInput
                    onMailTextElementChange={onMailTextElementUpdate('EN')}
                    mailTextElement={getMailTextElementByLanguage(
                        variable.texts,
                        'EN'
                    )}
                />
            </Box>
        </Fragment>
    );
};

type MailTextInputProps = {
    mailTextElement: MailTextElement;
    onMailTextElementChange: (newMailTextElement: MailTextElement) => void;
};

const MailTextInput: FunctionComponent<MailTextInputProps> = ({
    mailTextElement,
    onMailTextElementChange,
}) => {
    if (mailTextElement === null) return null;

    const [value, setValue] = useState(mailTextElement.customText);

    const onValueChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        setValue(value);
    }, []);

    const onBlur = useCallback(() => {
        onMailTextElementChange({
            ...mailTextElement,
            customText: value?.replace(/\n/g, ' ') || null,
        });
    }, [value]);

    useEffect(() => {
        if (mailTextElement.customText !== value) {
            setValue(mailTextElement.customText);
        }
    }, []);

    return (
        <Stack
            spacing={3}
            sx={{
                p: 2,
            }}
        >
            <Box>
                <Typography
                    sx={{
                        fontSize: '0.95rem',
                        fontWeight: 500,
                    }}
                >
                    Standardtext:
                </Typography>
                <Typography
                    sx={{
                        ml: 1,
                    }}
                >
                    {mailTextElement.defaultText}
                </Typography>
            </Box>
            <Box>
                <TextField
                    sx={{
                        width: '540px',
                        ml: 1,
                    }}
                    label='Neu:'
                    value={value || ''}
                    onChange={onValueChange}
                    onBlur={onBlur}
                    rows={3}
                    multiline
                />
            </Box>
        </Stack>
    );
};